import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { Box } from "@mui/system";
import {
  DataGridPro,
  GridToolbar,
  GridActionsCellItem,
} from "@mui/x-data-grid-pro";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { format } from "date-fns";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSnackbar } from "notistack";
import { GET_ALL_TRAININGS } from "../graphql/queries";
import { DELETE_TRAINING } from "../graphql/mutations";
import { AuthenticatedContext, PreferenceContext } from "../App";
import { ContainerPaper } from "../components";

const TrainingManagement = () => {
  const { preferences, setPreferences } = useContext(PreferenceContext);
  const { userRole, setLoading } = useContext(AuthenticatedContext);

  let trainingPref = { ...preferences.trainingMgmt };
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(trainingPref.pageSize || 10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogParams, setDeleteDialogParams] = useState({});
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);

  const [getAllTrainings, { testsLoading }] = useLazyQuery(GET_ALL_TRAININGS, {
    fetchPolicy: "no-cache",
    onCompleted: ({ getAllTrainings }) => {
      if (getAllTrainings?.trainings.length === 0) {
        setLoading(true);
      } else {
        setLoading(false);
        setRows(getAllTrainings.trainings);
      }
      setRowCount(getAllTrainings.total);
    },
  });

  const [deleteTraining] = useMutation(DELETE_TRAINING, {
    onError: (error) => {
      console.log(error);
      setDeleteDialogOpen(false);
    },
    onCompleted: ({ deleteTraining }) => {
      if (deleteTraining === true) {
        setRows(rows.filter((row) => row.id !== deleteDialogParams.id));
        enqueueSnackbar(
          `Test(id: ${deleteDialogParams.id}) has been successfully deleted`,
          {
            variant: "success",
          }
        );
      } else if (deleteTraining === false) {
        enqueueSnackbar("Test can not be found", {
          variant: "error",
        });
      }
      setDeleteDialogOpen(false);
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setLoading(true);
    getAllTrainings({
      variables: {
        skip: page * pageSize,
        take: pageSize,
        filters: trainingPref.filters,
        sort: trainingPref.sort,
      },
    });
  }, [
    page,
    pageSize,
    trainingPref.filters,
    trainingPref.sort,
    setLoading,
  ]);

  const handleDeleteTraining = (params) => {
    deleteTraining({
      variables: { id: params.id },
    });
  };

  const deleteTestFunction = useCallback(
    (params) => () => {
      setDeleteDialogOpen(true);
      setDeleteDialogParams(params.row);
    },
    []
  );

  const viewTest = useCallback(
    (params) => () =>
      history.push(`/trainingmanagement/${params.row.id}`, {
        data: params.row,
      }),
    [history]
  );

  const handlePageSizeChange = (newPageSize) => {
    trainingPref.pageSize = newPageSize;
    setPageSize(newPageSize);
    setPreferences({
      ...preferences,
      trainingMgmt: {
        ...trainingPref,
      },
    });
    if (page === 0) {
      getAllTrainings({
        variables: {
          skip: page * newPageSize,
          take: newPageSize,
          filters: trainingPref.filters,
          sort: trainingPref.sort,
        },
      });
    } else {
      setPage(0);
    }
  };

  const handleColumnWidthChange = (params) => {
    trainingPref.widths[params.colDef.field] = params.width;
    setPreferences({
      ...preferences,
      trainingMgmt: {
        ...trainingPref,
      },
    });
  };

  const handleColumnOrderChange = (params) => {
    trainingPref.columns.splice(params.oldIndex, 1);
    trainingPref.columns.splice(params.targetIndex, 0, params.field);
    setPreferences({
      ...preferences,
      trainingMgmt: {
        ...trainingPref,
      },
    });
  };

  const handleColumnVisibilityModelChange = (model) => {
    trainingPref.visible = { ...model };
    setPreferences({
      ...preferences,
      trainingMgmt: {
        ...trainingPref,
      },
    });
  };

  const handleSortModelChange = (model) => {
    trainingPref.sort = [...model];

    setPreferences({
      ...preferences,
      trainingMgmt: {
        ...trainingPref,
      },
    });
    if (page === 0) {
      getAllTrainings({
        variables: {
          skip: page * pageSize,
          take: pageSize,
          filters: trainingPref.filters,
          sort: [...model],
        },
      });
    } else {
      setPage(0);
    }
  };

  const handleFilterModelChange = (model) => {
    trainingPref.filters = { ...model };

    setPreferences({
      ...preferences,
      trainingMgmt: {
        ...trainingPref,
      },
    });
    if (page === 0) {
      getAllTrainings({
        variables: {
          skip: page * pageSize,
          take: pageSize,
          filters: { ...model },
          sort: trainingPref.sort,
        },
      });
    } else {
      setPage(0);
    }
  };

  const getApplyFilterFnName = (value) => {
    if (!value) {
      return null;
    }
    let re = new RegExp(value, "i");
    return (params) => {
      return re.test(params.value);
    };
  };

  const columns = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        disableClickEventBubbling: true, // fix Uncaught TypeError: Failed to execute 'contains' on 'Node'
        headerName: "Actions",
        width: trainingPref?.widths?.actions,
        headerAlign: "center",
        disableReorder: true,
        hideable: false,
        getApplyQuickFilterFn: undefined,
        getActions: (params) => {
          let actions = [
            <GridActionsCellItem
              icon={
                <Tooltip title="View Training">
                  <VisibilityIcon />
                </Tooltip>
              }
              label="View"
              color="primary"
              onClick={viewTest(params)}
            />,
          ];
          if (["Admin", "Education Consultant"].includes(userRole)) {
            actions.push(
              <GridActionsCellItem
                icon={
                  <Tooltip title="Delete Training">
                    <DeleteIcon />
                  </Tooltip>
                }
                label="Delete"
                color="primary"
                onClick={deleteTestFunction(params)}
              />
            );
          }
          return actions;
        },
      },
      {
        field: "id",
        headerName: "Training ID",
        width: trainingPref?.widths?.id,
        headerAlign: "center",
        align: "center",
        getApplyQuickFilterFn: undefined,
        hide:
          !trainingPref?.visible?.id ||
          !["Admin", "Education Consultant"].includes(userRole),
        type: "number",
      },
      {
        field: "name",
        headerName: "Training Name",
        width: trainingPref?.widths?.name,
        headerAlign: "center",
        align: "center",
        hide: !trainingPref?.visible?.name,
        getApplyQuickFilterFn: getApplyFilterFnName,
      },
      {
        field: "numEnrolled",
        headerName: "# Enrolled",
        width: trainingPref?.widths?.numEnrolled,
        headerAlign: "center",
        align: "center",
        getApplyQuickFilterFn: undefined,
        hide: !trainingPref?.visible?.numEnrolled,
        type: "number",
      },
      {
        field: "numActive",
        headerName: "# Active",
        width: trainingPref?.widths?.numActive,
        headerAlign: "center",
        align: "center",
        getApplyQuickFilterFn: undefined,
        hide: !trainingPref?.visible?.numActive,
        type: "number",
      },
      {
        field: "assessmentId",
        headerName: "Assessment ID",
        width: trainingPref?.widths?.assessmentId,
        headerAlign: "center",
        align: "center",
        getApplyQuickFilterFn: undefined,
        hide:
          !trainingPref?.visible?.assessmentId ||
          !["Admin", "Education Consultant"].includes(userRole),
        type: "number",
      },
      {
        field: "totalQuestions",
        headerName: "Total Questions",
        width: trainingPref?.widths?.totalQuestions,
        headerAlign: "center",
        align: "center",
        hide: !trainingPref?.visible?.totalQuestions,
        getApplyQuickFilterFn: undefined,
        type: "number",
      },
      {
        field: "firstGroupQuestions",
        headerName: "First Group Questions",
        width: trainingPref?.widths?.firstGroupQuestions,
        headerAlign: "center",
        align: "center",
        hide: !trainingPref?.visible?.firstGroupQuestions,
        getApplyQuickFilterFn: undefined,
        type: "number",
      },
      {
        field: "timeOfFirstGroupQuestions",
        headerName: "Time Of First Group Questions",
        width: trainingPref?.widths?.timeOfFirstGroupQuestions,
        headerAlign: "center",
        align: "center",
        hide: !trainingPref?.visible?.timeOfFirstGroupQuestions,
        getApplyQuickFilterFn: undefined,
        type: "number",
      },
      {
        field: "totalCorrectScoreSectionOne",
        headerName: "Total Correct Score Section One",
        width: trainingPref?.widths?.totalCorrectScoreSectionOne,
        headerAlign: "center",
        align: "center",
        hide: !trainingPref?.visible?.totalCorrectScoreSectionOne,
        getApplyQuickFilterFn: undefined,
        type: "number",
      },
      // {
      //   field: "totalTimeSectionOne",
      //   headerName: "Total Time Section One",
      //   width: trainingPref?.widths?.totalTimeSectionOne,
      //   headerAlign: "center",
      //   align: "center",
      //   hide: !trainingPref?.visible?.totalTimeSectionOne,
      //   getApplyQuickFilterFn: undefined,
      // },
      {
        field: "totalCorrectScoreSectionTwo",
        headerName: "Total Correct Score Section Two",
        width: trainingPref?.widths?.totalCorrectScoreSectionTwo,
        headerAlign: "center",
        align: "center",
        hide: !trainingPref?.visible?.totalCorrectScoreSectionTwo,
        getApplyQuickFilterFn: undefined,
        type: "number",
      },
      // {
      //   field: "totalTimeSectionTwo",
      //   headerName: "Total Time Section Two",
      //   width: trainingPref?.widths?.totalTimeSectionTwo,
      //   headerAlign: "center",
      //   align: "center",
      //   hide: !trainingPref?.visible?.totalTimeSectionTwo,
      //   getApplyQuickFilterFn: undefined,
      // },
      {
        field: "isActive",
        headerName: "Active?",
        width: trainingPref?.widths?.isActive,
        headerAlign: "center",
        align: "center",
        hide: !trainingPref?.visible?.isActive,
        getApplyQuickFilterFn: undefined,
        type: "boolean",
      },
      {
        field: "createdAt",
        headerName: "Created",
        width: trainingPref?.widths?.createdAt,
        headerAlign: "center",
        type: "dateTime",
        hide: !trainingPref?.visible?.createdAt,
        getApplyQuickFilterFn: undefined,
        valueFormatter: ({ value }) => {
          if (value) {
            return format(value, "dd/MM/yyyy");
          }
        },
        align: "center",
        filterable: false,
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        width: trainingPref?.widths?.updatedAt,
        headerAlign: "center",
        type: "dateTime",
        hide: !trainingPref?.visible?.updatedAt,
        getApplyQuickFilterFn: undefined,
        valueFormatter: ({ value }) => {
          if (value) {
            return format(value, "dd/MM/yyyy");
          }
        },
        align: "center",
        filterable: false,
      },
    ],
    [
      trainingPref?.widths?.actions,
      trainingPref?.widths?.id,
      trainingPref?.widths?.name,
      trainingPref?.widths?.numEnrolled,
      trainingPref?.widths?.numActive,
      trainingPref?.widths?.assessmentId,
      trainingPref?.widths?.totalQuestions,
      trainingPref?.widths?.firstGroupQuestions,
      trainingPref?.widths?.timeOfFirstGroupQuestions,
      trainingPref?.widths?.totalCorrectScoreSectionOne,
      trainingPref?.widths?.totalCorrectScoreSectionTwo,
      trainingPref?.widths?.isActive,
      trainingPref?.widths?.createdAt,
      trainingPref?.widths?.updatedAt,
      trainingPref?.visible?.id,
      trainingPref?.visible?.name,
      trainingPref?.visible?.numEnrolled,
      trainingPref?.visible?.numActive,
      trainingPref?.visible?.assessmentId,
      trainingPref?.visible?.totalQuestions,
      trainingPref?.visible?.firstGroupQuestions,
      trainingPref?.visible?.timeOfFirstGroupQuestions,
      trainingPref?.visible?.totalCorrectScoreSectionOne,
      trainingPref?.visible?.totalCorrectScoreSectionTwo,
      trainingPref?.visible?.isActive,
      trainingPref?.visible?.createdAt,
      trainingPref?.visible?.updatedAt,
      userRole,
      viewTest,
      deleteTestFunction,
    ]
  );

  return (
    <ContainerPaper>
      {}
      <Box
        sx={{
          display: "flex",
          mb: 3,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography color="primary" gutterBottom variant="h6" sx={{ mb: 0 }}>
          Training Management
        </Typography>
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {`Are you sure you want to delete this training?`}
        </DialogTitle>
        <DialogActions sx={{ mx: 1.6, my: 1 }}>
          <Button
            onClick={() => handleDeleteTraining(deleteDialogParams)}
            color="primary"
            variant="contained"
            startIcon={<CheckCircleIcon />}
            autoFocus
          >
            Confirm
          </Button>
          <Button
            color="secondary"
            variant="contained"
            startIcon={<CancelIcon />}
            onClick={() => setDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <DataGridPro
        rows={rows}
        columns={columns}
        components={{ Toolbar: GridToolbar }}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        pageSize={trainingPref.pageSize}
        onPageSizeChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        disableSelectionOnClick
        autoHeight
        loading={testsLoading}
        onColumnWidthChange={handleColumnWidthChange}
        onColumnOrderChange={handleColumnOrderChange}
        pagination
        pinnedColumns={{ left: ["actions"] }}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={handleFilterModelChange}
        filterModel={trainingPref.filters}
        sortModel={trainingPref.sort}
        page={page}
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        rowCount={rowCount}
        density="compact"
      />
    </ContainerPaper>
  );
};

export default TrainingManagement;
